// THE SEAM (plan §3.3).
//
// Everything above this file — the panel, the step machine, the draft — is
// provider-agnostic and survives untouched when the execution backend changes.
// Everything below it is an execution detail. There are exactly two drivers in
// the roadmap and this interface is what makes swapping them a swap and not a
// rewrite:
//
//   v0.5  mock          — never leaves the browser (this file)
//   v1    headless      — POST to our backend, which replays the engine's funnel
//   v2    native        — our own inventory, same call shape
//
// A supplier's own booking API is deliberately NOT on that list: depending on
// the counterparty's cooperation is the thing this architecture exists to avoid.

import type { BookingDraft } from "./BookingState";

export type BookingResult =
  | { status: "confirmed"; reference: string }
  | { status: "failed"; reason: string };

export interface BookingDriver {
  /**
   * Whether completing this booking asks the guest for card details.
   *
   * Declared by the driver rather than written into the copy, because "non ti
   * chiediamo la carta" is the strongest thing we can say at the moment of
   * hesitation — and the moment it stops being true, a hardcoded reassurance
   * becomes a lie the guest discovers one screen later. A driver that starts
   * collecting payment flips this and the reassurance disappears on its own.
   */
  readonly collectsPayment: boolean;

  /**
   * Carries a draft to its conclusion. Implementations must be idempotent on
   * the same draft id: a double submit is a user behaviour, not an edge case,
   * and it must never produce two reservations.
   */
  submit(draft: BookingDraft): Promise<BookingResult>;
}

/**
 * v0.5 driver. Resolves locally after a plausible delay and invents a
 * reference — it does not contact Scidoo, our backend, or anything else.
 *
 * This exists so the whole guest-facing flow can be built and judged before
 * the engine spike (P0) has answered whether the funnel is replayable at all.
 * The answer to that question changes this file and nothing above it.
 */
export const mockBookingDriver: BookingDriver = {
  collectsPayment: false,

  async submit(draft: BookingDraft): Promise<BookingResult> {
    await new Promise((resolve) => setTimeout(resolve, 1400));
    const stamp = draft.id.slice(-6).toUpperCase();
    return { status: "confirmed", reference: `MOCK-${stamp}` };
  },
};

export default mockBookingDriver;
